import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend
} from 'recharts';
import { Category } from '@mui/icons-material';

const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

const WasteCategoryChart = ({ categories = [], loading = false }) => {
    // Calculate proper percentages that sum to 100%
    const totalCount = categories.reduce((sum, cat) => sum + (cat.count || 0), 0);

    const chartData = categories.map((cat, index) => ({
        name: cat.categoryName || cat.name || `Category ${index + 1}`,
        value: cat.count || 0,
        percentage: totalCount > 0 ? ((cat.count || 0) / totalCount * 100).toFixed(1) : 0,
        color: COLORS[index % COLORS.length]
    })).filter(item => item.value > 0);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            return (
                <Box sx={{
                    background: 'white',
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    boxShadow: 2
                }}>
                    <Typography variant="body2" fontWeight={600}>
                        {data.payload.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Count: {data.payload.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Percentage: {data.payload.percentage}%
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>Loading category data...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Category sx={{ color: '#43a047', mr: 1, fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                            Waste Category Distribution
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Total Classifications: {totalCount}
                        </Typography>
                    </Box>
                </Box>

                {chartData.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Category sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No classification data available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Start classifying waste to see category breakdown
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percentage }) => `${name}: ${percentage}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default WasteCategoryChart;
